/**
 * Shared well-pump-rate derivation for Phreatic.
 *
 * Single source of truth for turning a well's DTW gauging history into an
 * effective sustained pump rate (GPM) via inverse Theis (Cooper-Jacob).
 * Used by both the Cone of Depression tab and the Sustainable Yield panel so
 * the two surfaces always agree on what each well is actually pulling.
 *
 * Why derive instead of asking for a use case: V5 inference already embeds a
 * well's historical pumping signature (it trains on the DTW gauging). Layering
 * a use-case pump rate on top double-counts the same physical decline. Reading
 * the rate back out of the same gauging keeps every downstream calc consistent.
 */

// Per-aquifer hydraulic conductivity (ft/day) and storativity (dimensionless).
export const AQUIFER_K: Record<string, number> = {
  Ogallala: 25, Edwards: 50, 'Edwards-Trinity': 30, 'Gulf Coast': 15,
  'Carrizo-Wilcox': 10, 'Pecos Valley': 20, 'Seymour': 25, Trinity: 12, other: 20,
};
export const AQUIFER_S: Record<string, number> = {
  Ogallala: 0.17, Edwards: 0.05, 'Edwards-Trinity': 0.10, 'Gulf Coast': 0.15,
  'Carrizo-Wilcox': 0.12, 'Pecos Valley': 0.15, 'Seymour': 0.20, Trinity: 0.08, other: 0.15,
};

// Gates for trusting a gauging-derived Q. Below these, fall back to manual.
export const MIN_GAUGING_READINGS = 6;
export const MIN_GAUGING_SPAN_DAYS = 730; // ~2 years

// acre-foot = 325,851 gal; minutes per year = 365.25 * 24 * 60.
const GAL_PER_AF = 325851;
const MIN_PER_YR = 525960;

/** Convert an annual AF demand to an average continuous pump rate (GPM). */
export function afYrToGpm(afPerYear: number): number {
  return afPerYear * GAL_PER_AF / MIN_PER_YR;
}

// Manual use-case demand — water duty per unit per year. Mirrors the backend
// DEMAND_TABLE so the manual fallback matches New Developments / Economics.
// Used only when a well has no gauging-derived Q and no manual asset rate:
// the user enters a use case + quantity + well count, and we split the demand
// evenly into a per-well GPM for Theis.
export const USE_CASE_DEMAND: Record<string, { label: string; unit: string; af_per_unit: number; group: string }> = {
  corn:            { label: 'Corn',            unit: 'acres',      af_per_unit: 2.0,     group: 'Agriculture' },
  sorghum:         { label: 'Sorghum',         unit: 'acres',      af_per_unit: 1.1,     group: 'Agriculture' },
  cotton:          { label: 'Cotton',          unit: 'acres',      af_per_unit: 1.5,     group: 'Agriculture' },
  alfalfa:         { label: 'Alfalfa',         unit: 'acres',      af_per_unit: 3.5,     group: 'Agriculture' },
  wheat:           { label: 'Wheat',           unit: 'acres',      af_per_unit: 0.8,     group: 'Agriculture' },
  soybeans:        { label: 'Soybeans',        unit: 'acres',      af_per_unit: 1.4,     group: 'Agriculture' },
  cattle_feedyard: { label: 'Cattle Feedyard', unit: 'head',       af_per_unit: 0.0134,  group: 'Livestock' },
  dairy:           { label: 'Dairy',           unit: 'head',       af_per_unit: 0.0392,  group: 'Livestock' },
  poultry:         { label: 'Poultry',         unit: 'head',       af_per_unit: 0.00056, group: 'Livestock' },
  swine:           { label: 'Swine',           unit: 'head',       af_per_unit: 0.00672, group: 'Livestock' },
  data_center_mw:  { label: 'Data Center',     unit: 'MW',         af_per_unit: 560.0,   group: 'Industrial' },
  oil_gas_frac:    { label: 'Oil & Gas (Frac)',unit: 'wells',      af_per_unit: 15.3,    group: 'Industrial' },
  ethanol_plant:   { label: 'Ethanol Plant',   unit: 'MGY',        af_per_unit: 1095.0,  group: 'Industrial' },
  municipal:       { label: 'Municipal',       unit: 'population',  af_per_unit: 0.112,   group: 'Municipal' },
};

export interface ManualDemand {
  useCase: string;
  quantity: number;
  numWells: number;
}

/** Total annual demand (AF/yr) for a manual use-case config. */
export function manualDemandAfYr(cfg: ManualDemand): number {
  const afPerUnit = USE_CASE_DEMAND[cfg.useCase]?.af_per_unit || 0;
  return afPerUnit * (cfg.quantity || 0);
}

/** Per-well GPM for a manual config: total demand split across the wells. */
export function manualPerWellGpm(cfg: ManualDemand): number {
  const n = cfg.numWells || 0;
  if (n <= 0) return 0;
  return afYrToGpm(manualDemandAfYr(cfg) / n);
}

/** Forward Theis drawdown at distance r from a well pumping Q_gpm. */
export function theisDrawdown(
  Q_gpm: number, satFt: number, K: number, S: number, r_ft: number, t_days: number,
): number {
  if (r_ft <= 0 || t_days <= 0 || satFt <= 0) return 0;
  const Q = Q_gpm * 192.5; // GPM to ft³/day
  const T = K * satFt;
  const u = (r_ft * r_ft * S) / (4 * T * t_days);
  if (u > 20) return 0;
  const Wu = -0.5772 - Math.log(Math.max(u, 1e-15)) + u - (u * u) / 4 + (u * u * u) / 18;
  return (Q / (4 * Math.PI * T)) * Math.max(Wu, 0);
}

/** Linear-regression DTW trend over a series of gauging snapshots. */
export function dtwDeclineTrend(
  gaugings: Array<{ date: string | null; depth_to_water_ft: number | null }>,
): { decline_ft: number; span_days: number; n: number } | null {
  const valid = gaugings.filter(
    (g): g is { date: string; depth_to_water_ft: number } =>
      !!g.date && g.depth_to_water_ft != null && isFinite(g.depth_to_water_ft),
  );
  if (valid.length < 2) return null;
  const t0 = new Date(valid[0].date).getTime();
  const pts = valid.map((g) => ({
    t: (new Date(g.date).getTime() - t0) / 86400000,
    y: g.depth_to_water_ft,
  }));
  const n = pts.length;
  const mT = pts.reduce((s, p) => s + p.t, 0) / n;
  const mY = pts.reduce((s, p) => s + p.y, 0) / n;
  let num = 0;
  let den = 0;
  for (const p of pts) {
    num += (p.t - mT) * (p.y - mY);
    den += (p.t - mT) * (p.t - mT);
  }
  if (den === 0) return null;
  const slope = num / den; // ft/day, positive = water table dropping
  const span_days = pts[n - 1].t - pts[0].t;
  return { decline_ft: slope * span_days, span_days, n };
}

/**
 * Theis inversion (Cooper-Jacob): given a total observed drawdown over time at
 * the well, solve for the effective sustained pump rate Q in GPM.
 *   s = Q/(4πT)·W(u),  u = r²S/(4Tt),  W(u) ≈ -γ - ln(u)  for u << 1.
 * Returns 0 if inputs are out of range (no force-fit).
 */
export function theisInverseQ(
  decline_ft: number, satFt: number, K: number, S: number, t_days: number,
): number {
  if (decline_ft <= 0 || satFt <= 0 || t_days <= 0) return 0;
  const T = K * satFt; // ft²/day
  const r = 0.5; // standard well radius, ft
  const u = (r * r * S) / (4 * T * t_days);
  if (u >= 0.05) return 0; // Cooper-Jacob assumption breaks down
  const W = -0.5772 - Math.log(u);
  if (W <= 0) return 0;
  const Q_ft3_per_day = (decline_ft * 4 * Math.PI * T) / W;
  const Q_gpm = Q_ft3_per_day / 192.5;
  return Q_gpm > 0 && isFinite(Q_gpm) ? Q_gpm : 0;
}

export type GpmSource = 'derived' | 'manual' | 'manual_use_case' | 'none';

export interface WellGpmResult {
  gpm: number;
  source: GpmSource;
  n_readings: number;
  span_days: number;
}

/**
 * Resolve a single well's effective Q. Priority:
 *   1. Derived from gauging (if enough readings/span and the Theis fit is sane)
 *   2. Manual pump_rate_gpm on the asset record
 *   3. Manual use-case fallback (per-well GPM from a use case + quantity + well
 *      count, for sites with no gauging at all) — passed in by the caller
 *   4. None (gpm = 0 — caller excludes the well)
 */
export function deriveWellGpm(
  asset: { pump_rate_gpm?: number | null; well_depth_ft?: number | null; current_dtw_ft?: number | null },
  gaugings: Array<{ date: string | null; depth_to_water_ft: number | null }>,
  aquifer: string,
  manualUseCaseGpm: number = 0,
): WellGpmResult {
  const manualQ = Number(asset.pump_rate_gpm) || 0;
  const totalDepth = Number(asset.well_depth_ft) || 300;
  const currentDtw = Number(asset.current_dtw_ft) || 0;
  const satFt = Math.max(0, totalDepth - currentDtw);
  const K = AQUIFER_K[aquifer] || 25;
  const S = AQUIFER_S[aquifer] || 0.17;

  const trend = dtwDeclineTrend(gaugings);
  if (
    trend &&
    trend.n >= MIN_GAUGING_READINGS &&
    trend.span_days >= MIN_GAUGING_SPAN_DAYS &&
    trend.decline_ft > 0
  ) {
    const derived = theisInverseQ(trend.decline_ft, satFt, K, S, trend.span_days);
    if (derived > 0) {
      return { gpm: derived, source: 'derived', n_readings: trend.n, span_days: trend.span_days };
    }
  }
  if (manualQ > 0) {
    return { gpm: manualQ, source: 'manual', n_readings: 0, span_days: 0 };
  }
  if (manualUseCaseGpm > 0) {
    return { gpm: manualUseCaseGpm, source: 'manual_use_case', n_readings: 0, span_days: 0 };
  }
  return { gpm: 0, source: 'none', n_readings: 0, span_days: 0 };
}
